import React, { useCallback, useEffect, useState } from 'react';
import { GripVertical, X } from 'lucide-react';

/** Pixels from edit-mode row box `left` to first text glyph: grip padding (2+2) + icon 14 + flex gap 4 + input border 1 + input padding-left 6 */
const LYRIC_EDIT_TEXT_ORIGIN_OFFSET_PX = 2 + 14 + 2 + 4 + 1 + 6;

/** Last lyric with startBeat <= beat (karaoke-style “current” line). */
function pickActiveLyricId(lyrics, beat) {
  const t = Number(beat);
  const playhead = Number.isFinite(t) ? Math.max(0, t) : 0;
  const withText = lyrics.filter((l) => String(l.text ?? '').trim());
  if (!withText.length) return null;
  const sorted = [...withText].sort((a, b) => {
    const da = Number(a.startBeat) || 0;
    const db = Number(b.startBeat) || 0;
    if (da !== db) return da - db;
    return String(a.id).localeCompare(String(b.id));
  });
  let candidate = null;
  for (const line of sorted) {
    const start = Number(line.startBeat) || 0;
    if (playhead >= start) candidate = line.id;
    else break;
  }
  return candidate;
}

/**
 * Timeline-aligned lyrics lane: click empty space to add a line at that beat;
 * edit mode: inputs, drag handle, delete; view mode: plain text only.
 */
export function LyricsTimelineRow({
  height,
  timelineWidthPx,
  pixelsPerBeat,
  scrollParentRef,
  snapBeatToGrid,
  snapToGridEnabled,
  lyrics,
  onAddAtBeat,
  onChangeText,
  onTextCommit,
  onDelete,
  onMoveLine,
  backgroundImage,
  backgroundSize,
  hint,
  dragHint,
  editMode = true,
  /** Beat position of playhead (React state); used when transport is not animating every frame */
  playheadBeat = 0,
  /** Ref to same beat, updated every animation frame during playback — keeps highlight in sync with playhead */
  playheadBeatRef,
  /** True while playhead advances every frame (play / record / recording playback) */
  playheadAnimating = false,
}) {
  const [dragPreview, setDragPreview] = useState(null);
  const [viewHighlightId, setViewHighlightId] = useState(null);

  useEffect(() => {
    if (!editMode) setDragPreview(null);
  }, [editMode]);

  useEffect(() => {
    if (editMode || playheadAnimating) return;
    setViewHighlightId(pickActiveLyricId(lyrics, playheadBeat));
  }, [editMode, playheadAnimating, playheadBeat, lyrics]);

  useEffect(() => {
    if (editMode || !playheadAnimating || !playheadBeatRef) return;
    let frame;
    const tick = () => {
      const beat = playheadBeatRef.current;
      const next = pickActiveLyricId(lyrics, beat);
      setViewHighlightId((prev) => (prev === next ? prev : next));
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [editMode, playheadAnimating, lyrics, playheadBeatRef]);

  useEffect(() => {
    if (editMode) setViewHighlightId(null);
  }, [editMode]);

  const handlePaneClick = (e) => {
    if (!editMode) return;
    if (e.target !== e.currentTarget) return;
    const parent = scrollParentRef?.current;
    const sl = parent?.scrollLeft ?? 0;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left + sl;
    const rawBeat = Math.max(0, x / pixelsPerBeat);
    const beat = snapToGridEnabled ? snapBeatToGrid(rawBeat) : rawBeat;
    onAddAtBeat(beat);
  };

  const startLyricDrag = useCallback(
    (e, line) => {
      if (!editMode || e.button !== 0 || !onMoveLine) return;
      e.preventDefault();
      e.stopPropagation();
      const startBeat = Number(line.startBeat) || 0;
      const startX = e.clientX;

      const beatFromClientX = (clientX) => {
        const dx = clientX - startX;
        let nb = Math.max(0, startBeat + dx / pixelsPerBeat);
        if (snapToGridEnabled) nb = snapBeatToGrid(nb);
        return nb;
      };

      const onMove = (ev) => {
        setDragPreview({ id: line.id, beat: beatFromClientX(ev.clientX) });
      };

      const onUp = (ev) => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        setDragPreview(null);
        const nb = beatFromClientX(ev.clientX);
        if (Math.abs(nb - startBeat) > 1e-6) onMoveLine(line.id, nb);
      };

      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
      setDragPreview({ id: line.id, beat: startBeat });
    },
    [editMode, pixelsPerBeat, snapBeatToGrid, snapToGridEnabled, onMoveLine]
  );

  /** Beat anchor in px: where the lyric *text* should start (matches view mode). */
  const textAnchorPx = (line) => {
    const b =
      dragPreview?.id === line.id ? dragPreview.beat : Number(line.startBeat) || 0;
    return b * pixelsPerBeat;
  };

  const textAnchorPxView = (line) => (Number(line.startBeat) || 0) * pixelsPerBeat;

  const inputWidthPx = (text) => {
    const len = text?.length ?? 0;
    const approxChar = 7.2;
    const base = Math.max(len, 1);
    return Math.min(130, Math.max(36, Math.round(base * approxChar + 20)));
  };

  return (
    <div
      onClick={handlePaneClick}
      style={{
        position: 'sticky',
        top: 40,
        zIndex: 19,
        width: `${timelineWidthPx}px`,
        height,
        minHeight: height,
        boxSizing: 'border-box',
        backgroundColor: '#111c2e',
        borderBottom: '1px solid #334155',
        backgroundImage,
        backgroundSize,
        cursor: 'default',
        flexShrink: 0,
      }}
    >
      {hint && editMode && (
        <div
          style={{
            position: 'absolute',
            left: 8,
            top: 2,
            fontSize: 10,
            color: '#64748b',
            pointerEvents: 'none',
            zIndex: 0,
          }}
        >
          {hint}
        </div>
      )}
      {lyrics.map((line) => {
        if (!editMode) {
          const text = String(line.text ?? '').trim();
          if (!text) return null;
          const isCurrent = line.id === viewHighlightId;
          return (
            <div
              key={line.id}
              aria-current={isCurrent ? 'true' : undefined}
              style={{
                position: 'absolute',
                left: `${textAnchorPxView(line)}px`,
                top: 14,
                zIndex: 2,
                maxWidth: 'none',
                fontSize: 12,
                color: isCurrent ? '#fcd34d' : '#e2e8f0',
                fontWeight: isCurrent ? 600 : 400,
                whiteSpace: 'nowrap',
                userSelect: 'text',
                lineHeight: '18px',
                textShadow: isCurrent ? '0 0 10px rgba(251, 191, 36, 0.65)' : 'none',
                transition: 'color 0.05s ease, text-shadow 0.05s ease',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {text}
            </div>
          );
        }

        return (
          <div
            key={line.id}
            style={{
              position: 'absolute',
              left: `${Math.max(0, textAnchorPx(line) - LYRIC_EDIT_TEXT_ORIGIN_OFFSET_PX)}px`,
              top: 14,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
              zIndex: 2,
              maxWidth: 'none',
            }}
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => {
              if (e.button !== 0) return;
              if (e.target.closest('input, button')) return;
              startLyricDrag(e, line);
            }}
            title={dragHint}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                flexShrink: 0,
                padding: '0 2px',
                cursor: 'grab',
                color: '#64748b',
              }}
              aria-hidden
            >
              <GripVertical size={14} strokeWidth={2} />
            </div>
            <input
              type="text"
              value={line.text}
              placeholder="…"
              onChange={(e) => onChangeText(line.id, e.target.value)}
              onBlur={() => onTextCommit?.()}
              onMouseDown={(e) => e.stopPropagation()}
              style={{
                width: `${inputWidthPx(line.text)}px`,
                fontSize: 12,
                padding: '2px 6px',
                borderRadius: 4,
                border: '1px solid #475569',
                backgroundColor: '#0f172a',
                color: '#e2e8f0',
                outline: 'none',
                boxSizing: 'border-box',
              }}
            />
            <button
              type="button"
              title="Remove line"
              onClick={() => onDelete(line.id)}
              style={{
                padding: 2,
                border: 'none',
                borderRadius: 4,
                background: '#334155',
                color: '#94a3b8',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <X size={14} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
